(() => {
  class FitmentCategoryNavigation {
    constructor(root, { onSelect } = {}) {
      this.root = root;
      this.onSelect = onSelect;
      this.shell = root.querySelector("[data-fitment-category-nav-shell]");
      this.track =
        root.querySelector("[data-fitment-category-nav-track]") ||
        root.querySelector(".fitment-category-nav");
      this.items = [...root.querySelectorAll("[data-fitment-category-nav]")];
      this.scrollButtons = [
        ...root.querySelectorAll("[data-fitment-category-nav-scroll]"),
      ];
      this.mobileProgress = root.querySelector(
        "[data-parts-category-nav-progress]",
      );
      this.mobileProgressCurrent = root.querySelector(
        "[data-parts-category-progress-current]",
      );
      this.mobileProgressTotal = root.querySelector(
        "[data-parts-category-progress-total]",
      );
      this.mobileProgressDirection = root.querySelector(
        "[data-parts-category-progress-direction]",
      );
      this.available = Boolean(this.shell && this.track);

      if (!this.available) return;

      this.bindNavigation();
      this.bindScrollControls();
      this.bindProgressSwipe();
    }

    bindNavigation() {
      this.items.forEach((item) => {
        item.addEventListener("click", (event) => {
          event.preventDefault();
          this.onSelect?.(item.dataset.target, item);
        });
      });
    }

    bindScrollControls() {
      const updateControls = () => this.updateScrollControls();

      this.track.addEventListener("scroll", updateControls, {
        passive: true,
      });

      this.scrollButtons.forEach((button) => {
        button.addEventListener("click", () => {
          const direction =
            button.dataset.fitmentCategoryNavScroll === "prev" ? -1 : 1;
          const distance = Math.max(this.track.clientWidth * 0.72, 180);
          this.track.scrollBy({
            left: direction * distance,
            behavior: "smooth",
          });
        });
      });

      window.addEventListener("resize", updateControls);
      window.addEventListener("load", updateControls, { once: true });
      window.requestAnimationFrame(updateControls);
    }

    bindProgressSwipe() {
      if (!this.mobileProgress) return;

      let activePointerId = null;
      let startX = 0;
      let startScrollLeft = 0;

      const stopDragging = (event) => {
        if (
          activePointerId === null ||
          (event && event.pointerId !== activePointerId)
        ) {
          return;
        }

        activePointerId = null;
        this.mobileProgress.classList.remove("is-dragging");
      };

      this.mobileProgress.addEventListener("pointerdown", (event) => {
        if (
          activePointerId !== null ||
          (event.pointerType === "mouse" && event.button !== 0)
        ) {
          return;
        }

        activePointerId = event.pointerId;
        startX = event.clientX;
        startScrollLeft = this.track.scrollLeft;
        this.mobileProgress.classList.add("is-dragging");

        try {
          this.mobileProgress.setPointerCapture(event.pointerId);
        } catch (_error) {
          // Synthetic pointer events may not support pointer capture.
        }
      });

      this.mobileProgress.addEventListener("pointermove", (event) => {
        if (event.pointerId !== activePointerId) return;

        const distance = event.clientX - startX;
        if (Math.abs(distance) < 2) return;

        this.track.scrollLeft = startScrollLeft - distance;
        if (event.cancelable) event.preventDefault();
      });

      this.mobileProgress.addEventListener("pointerup", stopDragging);
      this.mobileProgress.addEventListener("pointercancel", stopDragging);
      this.mobileProgress.addEventListener("lostpointercapture", stopDragging);
    }

    setActive(panelId) {
      if (!this.available) return;

      this.items.forEach((item) => {
        item.classList.toggle("is-active", item.dataset.target === panelId);
      });
      this.updateScrollControls();
    }

    updateStickyState(stickyTop) {
      if (!this.available) return;

      const isDesktop = window.matchMedia("(min-width: 992px)").matches;
      const isStuck =
        isDesktop &&
        window.scrollY > 0 &&
        this.shell.getBoundingClientRect().top <= stickyTop + 1;
      this.shell.classList.toggle("is-stuck", isStuck);
    }

    updateScrollControls() {
      if (!this.available) return;

      const maxScroll = Math.max(
        0,
        this.track.scrollWidth - this.track.clientWidth,
      );
      const currentScroll = this.track.scrollLeft;
      const hasOverflow = maxScroll > 2;
      const canScrollPrev = currentScroll > 2;
      const canScrollNext = currentScroll < maxScroll - 2;

      this.shell.classList.toggle("has-overflow", hasOverflow);
      this.shell.classList.toggle("can-scroll-prev", canScrollPrev);
      this.shell.classList.toggle("can-scroll-next", canScrollNext);

      this.scrollButtons.forEach((button) => {
        const isPrevious = button.dataset.fitmentCategoryNavScroll === "prev";
        button.disabled = isPrevious ? !canScrollPrev : !canScrollNext;
      });

      this.updateMobileProgress(maxScroll, currentScroll);
    }

    updateMobileProgress(maxScroll, currentScroll) {
      if (!this.mobileProgress || !this.items.length) return;

      const total = this.items.length;
      const atStart = currentScroll <= 2;
      const atEnd = maxScroll > 2 && currentScroll >= maxScroll - 2;
      const scrollRatio = maxScroll > 2 ? currentScroll / maxScroll : 0;
      const fillProgress = scrollRatio * 100;
      let currentCategory = 1;

      if (atEnd) {
        currentCategory = total;
      } else if (!atStart) {
        const trackRect = this.track.getBoundingClientRect();
        const trackCenter = trackRect.left + trackRect.width / 2;
        let nearestDistance = Number.POSITIVE_INFINITY;

        this.items.forEach((item, index) => {
          const itemRect = item.getBoundingClientRect();
          const itemCenter = itemRect.left + itemRect.width / 2;
          const distance = Math.abs(itemCenter - trackCenter);

          if (distance < nearestDistance) {
            nearestDistance = distance;
            currentCategory = index + 1;
          }
        });
      }

      const direction = atStart ? "start" : atEnd ? "end" : "middle";

      this.mobileProgress.style.setProperty(
        "--parts-category-scroll-progress",
        `${Math.min(100, Math.max(0, fillProgress)).toFixed(2)}%`,
      );
      this.mobileProgress.dataset.scrollDirection = direction;
      if (this.mobileProgressDirection) {
        this.mobileProgressDirection.dataset.partsCategoryProgressDirection =
          direction;
      }
      if (this.mobileProgressCurrent) {
        this.mobileProgressCurrent.textContent = String(currentCategory);
      }
      if (this.mobileProgressTotal) {
        this.mobileProgressTotal.textContent = String(total);
      }
    }
  }

  window.DPPFitmentCategoryNavigation = FitmentCategoryNavigation;
})();
